import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { IntlProvider } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, where, query, collection } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  getLoggedUserProps,
  fetchFriends,
  fetchRequests,
  fetchAllRequests,
  fetchStatsByUser,
  fetchFields,
  updateUser as updateUserDoc,
} from './api';
import { addMessagingTokenToUser } from '../login/utils';
import { onCollectionChange } from '../utils';
import { SPORTS, ROLES, POSITION, LEVELS, LANGUAGES } from '../constants';
import { listenToConversations } from '../chat/api';

import esJSON from '../locale/es.json';
import enJSON from '../locale/en.json';
import arJSON from '../locale/ar.json';

export const StoreContext = createContext(null);

const getMessages = lang => {
  if (lang?.includes('ar')) return arJSON;
  if (lang?.includes('en')) return enJSON;
  return esJSON;
};

const Store = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  // Mirrors user.friendsIds so the user-doc listener can detect add/remove
  // without re-subscribing on every friends change.
  const friendsIdsRef = useRef([]);
  const [language, setLanguage] = useState(navigator.language);
  const [messages, setMessages] = useState(esJSON);

  const [allRequests, setAllRequests] = useState(null);
  const [requestsChanged, setRequestsChanged] = useState(null);
  const [updateUser, setUpdateUser] = useState(false);
  const [isStoreLoading, setIsStoreLoading] = useState(true);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [sports, setSports] = useState([SPORTS.FUTBOL, SPORTS.PADEL]);
  const [placesChanged, setPlacesChanged] = useState(null);
  const [fields, setFields] = useState([]);
  const [userPlacesSnapshot, setUserPlacesSnapshot] = useState(null);
  const [userPlaces, setUserPlaces] = useState([]);
  const [conversations, setConversations] = useState([]);
  // Live set of userIds whose CURRENT role is manager/admin — used to badge
  // matches as "official" (OKTA) regardless of when the match was created.
  const [officialUserIds, setOfficialUserIds] = useState(new Set());
  const [chatState, setChatState] = useState({
    open: false,
    conversationId: null,
    recipientUid: null,
    recipientNickname: '',
    recipientPhotoURL: '',
    recipientToken: null,
  });
  // Estado del ConversationsDrawer subido al store para que se pueda
  // abrir desde el perfil (el boton del navbar fue removido — el chat
  // 1-a-1 casi no se usa; la gente sigue avisando por WhatsApp).
  const [conversationsOpen, setConversationsOpen] = useState(false);

  const openChat = useCallback(params => {
    setChatState({ open: true, ...params });
  }, []);

  const closeChat = useCallback(() => {
    setChatState(prev => ({ ...prev, open: false }));
  }, []);

  const openConversations = useCallback(() => setConversationsOpen(true), []);
  const closeConversations = useCallback(() => setConversationsOpen(false), []);
  useEffect(() => {
    setMessages(getMessages(language));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const condition = user.isAdmin ? undefined : where('to', '==', user.email);
    const unsubscribe = onCollectionChange('requests', setRequestsChanged, condition);
    return () => unsubscribe();
  }, [user?.uid, user?.isAdmin]);

  useEffect(() => {
    if (!user) return;
    if (user.isAdmin) {
      fetchAllRequests().then(p => setAllRequests(p));
    } else {
      fetchRequests().then(p => setAllRequests(p));
    }
  }, [requestsChanged, user?.uid]);

  useEffect(() => {
    if (!user) return undefined;
    return onCollectionChange('fields', setPlacesChanged);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    // Unified `fields` collection: docs without a userId are global places
    // created by an admin (visible to everyone). Personal favorites carry the
    // owner's userId and are loaded separately via the userPlaces listener.
    fetchFields().then(p => setFields(p.filter(f => !f.userId)));
  }, [placesChanged, user?.uid]);

  // Real-time subscription to the current user's personal places (own favorites)
  useEffect(() => {
    if (!user?.uid) return undefined;
    return onCollectionChange('fields', setUserPlacesSnapshot, where('userId', '==', user.uid));
  }, [user?.uid]);

  useEffect(() => {
    if (!userPlacesSnapshot) return;
    setUserPlaces(userPlacesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [userPlacesSnapshot]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return listenToConversations(user.uid, setConversations);
  }, [user?.uid]);

  // Keep the official (manager/admin) userId set live so match badges reflect
  // a user's CURRENT role, including matches created before they got the role.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const q = query(collection(db, 'users'), where('role', 'in', ['manager', 'admin', 'owner']));
    return onSnapshot(q, snapshot => {
      setOfficialUserIds(new Set(snapshot.docs.map(d => d.id)));
    }, err => {
      if (err.code !== 'permission-denied') console.error('[Firestore] officialUserIds listener:', err.code);
    });
  }, [user?.uid]);

  const setUpUser = async loggedUser => {
    if (loggedUser === null) {
      navigate('/login');
    }
    setIsUserLoading(true);
    try {
      const userProps = await getLoggedUserProps();
      if (!userProps || userProps === null) {
        navigate('/login');
        return;
      }
      const requests = await fetchRequests().catch(() => []);

      if (userProps.friendsIds) {
        await fetchFriends(userProps.friendsIds).then(data => {
          userProps.friends = data;
        }).catch(() => { userProps.friends = []; });
      } else userProps.friends = [];

      await fetchStatsByUser(loggedUser.uid).then(data => {
        let goals = 0;
        let absent = 0;
        data.forEach(d => {
          goals += d.goals;
          if (d.absent) {
            absent++;
          }
        });
        if (userProps) {
          userProps.goals = goals;
          userProps.absent = absent;
        }
      }).catch(() => {});

      // Patch missing fields in Firestore so they don't stay undefined
      const missingDefaults = {};
      if (!userProps.uid || userProps.uid !== loggedUser.uid) {
        missingDefaults.uid = loggedUser.uid;
      }
      if (!userProps.nickname) {
        const fallbackNickname =
          loggedUser.displayName || loggedUser.email?.split('@')[0] || '';
        if (fallbackNickname) missingDefaults.nickname = fallbackNickname;
      }
      if (userProps.age === undefined) missingDefaults.age = 0;
      if (!userProps.position) missingDefaults.position = POSITION.DEFENCE;
      if (!userProps.level) missingDefaults.level = LEVELS.BASIC;
      if (!userProps.languageSelected) {
        missingDefaults.languageSelected = LANGUAGES.SPANISH;
      }
      if (userProps.tokens === undefined) missingDefaults.tokens = 0;
      if (!userProps.friendsIds) missingDefaults.friendsIds = [];

      if (Object.keys(missingDefaults).length > 0) {
        // Apply defaults in-memory immediately so setUser always gets clean values
        Object.assign(userProps, missingDefaults);
        // Best-effort write to Firestore using the actual document ID
        updateUserDoc(userProps.id, missingDefaults).catch(() => {});
      }

      // 'manager' (formerly 'owner') is a match-manager role. Legacy 'owner'
      // docs are treated as manager until an admin re-assigns them.
      const isManager = userProps.role === ROLES.MANAGER || userProps.role === 'owner';
      const isAdmin = userProps.role === ROLES.ADMIN;
      // Can create/manage ANY match (not just their own): managers and admins.
      // The per-match creator check (shift.userId === user.uid) is separate and
      // always lets the creator manage their own match regardless of role.
      const canManageMatches = isManager || isAdmin;
      await addMessagingTokenToUser(loggedUser.uid);
      if (userProps.languageSelected) {
        setMessages(getMessages(userProps.languageSelected));
      }
      setUser({
        ...loggedUser,
        firestoreId: userProps.id,
        role: userProps.role,
        games: userProps.games ?? 0,
        createdGames: userProps.createdGames ?? 0,
        nickname: userProps.nickname || loggedUser.displayName || loggedUser.email?.split('@')[0] || '',
        age: userProps.age,
        birthDate: userProps.birthDate || null,
        position: userProps.position,
        languageSelected: userProps.languageSelected,
        level: userProps.level,
        friends: userProps.friends,
        goals: userProps.goals ?? 0,
        absent: userProps.absent ?? 0,
        friendsIds: userProps.friendsIds ?? [],
        requests,
        // isOwner kept as an alias of isManager for backward compatibility with
        // existing consumers; new code should use isManager / canManageMatches.
        isOwner: isManager,
        isManager,
        isAdmin,
        canManageMatches,
        messageToken: userProps.messageToken || '',
        messageTokens: userProps.messageTokens ?? [],
        // Per-type notification preferences. Missing map / missing key defaults
        // to ON (opt-out model) — handled where it's read (profile + server).
        notificationPrefs: userProps.notificationPrefs ?? null,
        tokens: userProps.tokens ?? 0,
        photoURL: userProps.photoURL || loggedUser.photoURL || '',
        friendsList: userProps.friendsList ?? [],
        location: userProps.location ?? null,
        // Admin-managed lock flag. When true, App.jsx renders a lock screen
        // instead of the normal UI. Firestore rules also reject writes from
        // blocked accounts as a hard second layer.
        blocked: userProps.blocked === true,
      });
    } finally {
      setIsUserLoading(false);
    }
  };

  // Keep the friends ref in sync so the listener below can diff against it.
  useEffect(() => {
    friendsIdsRef.current = user?.friendsIds ?? [];
  }, [user?.friendsIds]);

  // Keep externally-mutated fields of the current user's doc in sync in
  // real-time: tokens, the notifications flag, and the friends list. The
  // friends part fixes the bug where a newly added friend only appeared after
  // a manual page refresh (the list is built from user.friends, which was
  // previously fetched only once).
  useEffect(() => {
    if (!user?.firestoreId) return undefined;
    const unsub = onSnapshot(doc(db, 'users', user.firestoreId), async snapshot => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();

      // Externally-mutated fields — copy through only when they changed.
      setUser(prev => {
        if (!prev) return prev;
        const nextTokens = data.tokens ?? 0;
        const nextPrefs = data.notificationPrefs ?? null;
        const prefsChanged =
          JSON.stringify(prev.notificationPrefs ?? null) !== JSON.stringify(nextPrefs);
        // Role can be changed by an admin from the roles panel — recompute the
        // capability flags live so it applies without a re-login.
        const nextRole = data.role;
        const roleChanged = prev.role !== nextRole;
        // Admin can block/unblock this user; propagate live so the lock screen
        // shows without a re-login (and unblocking releases the app instantly).
        const nextBlocked = data.blocked === true;
        const blockedChanged = prev.blocked !== nextBlocked;
        if (prev.tokens === nextTokens && !prefsChanged && !roleChanged && !blockedChanged) {
          return prev; // no-op if unchanged
        }
        const isManager = nextRole === ROLES.MANAGER || nextRole === 'owner';
        const isAdmin = nextRole === ROLES.ADMIN;
        return {
          ...prev,
          tokens: nextTokens,
          notificationPrefs: nextPrefs,
          role: nextRole,
          isManager,
          isOwner: isManager,
          isAdmin,
          canManageMatches: isManager || isAdmin,
          blocked: nextBlocked,
        };
      });

      // Friends list — refetch the friend docs when the id set changed
      // (added or removed) and update state live.
      const newIds = data.friendsIds ?? [];
      const oldIds = friendsIdsRef.current;
      const changed =
        newIds.length !== oldIds.length || newIds.some(id => !oldIds.includes(id));
      if (changed) {
        friendsIdsRef.current = newIds;
        const friends = await fetchFriends(newIds).catch(() => []);
        setUser(prev => (prev ? { ...prev, friendsIds: newIds, friends } : prev));
      }
    });
    return () => unsub();
  }, [user?.firestoreId]);

  useEffect(() => {
    onAuthStateChanged(auth, async loggedUser => {
      setIsStoreLoading(true);
      try {
        if (loggedUser) {
          await setUpUser(loggedUser);
        } else setUser(null);
      } finally {
        setIsStoreLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!updateUser) return;
    if (user) {
      setUpUser(user);
    }
    setUpdateUser(false);
  }, [updateUser]);

  const totalUnreadMessages = conversations.reduce(
    (acc, conv) => acc + (conv.unreadCount?.[user?.uid] ?? 0),
    0,
  );

  const store = {
    user: [user, setUser, setUpdateUser],
    sports: [sports, setSports],
    places: [fields, setFields],
    userPlaces: [userPlaces, setUserPlaces],
    allRequests: [allRequests, setAllRequests],
    isStoreLoading: [isStoreLoading, setIsStoreLoading],
    isUserLoading: [isUserLoading, setIsUserLoading],
    language: [language, setLanguage],
    conversations: [conversations, setConversations],
    chat: [chatState, openChat, closeChat],
    conversationsDrawer: [conversationsOpen, openConversations, closeConversations],
    totalUnreadMessages,
    officialUserIds,
  };

  const { Provider } = StoreContext;
  return (
    <Provider value={store}>
      <IntlProvider messages={messages} locale={language} defaultLocale="es">
        {children}
      </IntlProvider>
    </Provider>
  );
};

export default Store;
